import type { FrequencyCluster } from '@qi/audio-engine';
import { summarizeFace } from '@qi/biosignal';
import type { BaselineStats, SignalDeltas } from '@qi/resonance-engine';
import { copy, type ResonancePeak } from '@qi/shared';
import { buttonClasses, ScanProgress } from '@qi/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Volume2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SCAN_STEPS } from '../components/scan/steps.ts';
import { useResonanceTone } from '../components/scan/useResonanceTone.ts';
import { useScanFlowBlocker } from '../components/scan/useScanFlowBlocker.ts';
import { useSweepCapture } from '../components/scan/useSweepCapture.ts';
import { WebcamPreview } from '../components/scan/WebcamPreview.tsx';
import { FlowActionBar } from '../components/shell/FlowActionBar.tsx';
import { faceSummaryToSignalDeltas } from '../lib/biosignal-bridge.ts';
import { diag } from '../lib/diag.ts';
import { isSyntheticCapture } from '../lib/e2e.ts';
import { resolveClusterPeak } from '../lib/score-cluster.ts';
import {
  narrowClusters,
  rejectLowConfidence,
  selectNarrowWinners,
  selectTopK,
} from '../lib/sweep-narrowing.ts';
import { sweepProgress } from '../lib/sweep-progress.ts';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_app/scan/sweep')({
  component: SweepPage,
});

// Each band is held for 3 s — long enough for the orienting (pupil/blink)
// response to register, short enough to keep both modes inside their time
// budgets. Same dwell in broad and narrow passes so the read is comparable.
const BROAD_CLUSTER_MS = 3_000;
// Pre-roll before the first tone + measurement, so the user can rest their eyes
// on the focus dot before any sound starts. Skipped on the synthetic E2E path.
const PREROLL_MS = 3_000;
// Minimum pre-roll frames before we trust an in-situ re-center of the baseline.
// Below this the mean is too thin to beat the (stable) BioScan baseline.
const MIN_PREROLL_FRAMES = 20;
const NARROW_WINNERS = 3;
const NARROW_STEPS_PER_WINNER = 3;
const NARROW_CLUSTER_COUNT = NARROW_WINNERS * NARROW_STEPS_PER_WINNER; // 9

type ScanMode = 'basic' | 'advanced';

// Basic scan: the nine Solfeggio tones, one 3 s pass, no narrow refinement.
// 9 × 3 s = 27 s (≤ 30 s). Ids match the frequency-db catalog (`sol-174`…
// `sol-963`) so results resolve each band to its Solfeggio/chakra association.
const SOLFEGGIO_HZ = [174, 285, 396, 417, 528, 639, 741, 852, 963];
const SOLFEGGIO_BROAD: FrequencyCluster[] = SOLFEGGIO_HZ.map((hz) => ({
  id: `sol-${hz}`,
  centerHz: hz,
  durationMs: BROAD_CLUSTER_MS,
}));

// Advanced scan: log-spaced bands from 20 Hz to 1111 Hz (equal ratio per step,
// the "log method"), then a narrow pass that refines the strongest responders.
// 11 broad × 3 s = 33 s + narrow 27 s = 60 s total.
const ADVANCED_BROAD: FrequencyCluster[] = logBands(20, 1111, 11, BROAD_CLUSTER_MS, 'adv');
const ADVANCED_NARROW_TOTAL_MS = 27_000;

interface ScanModeConfig {
  broad: FrequencyCluster[];
  useNarrow: boolean;
  /** Fixed wall-clock budget for the whole narrow pass (0 when none). */
  narrowTotalMs: number;
  /** Constant denominator for the "Step X of N" counter, known up front. */
  totalClusters: number;
}

const MODE_CONFIG: Record<ScanMode, ScanModeConfig> = {
  basic: {
    broad: SOLFEGGIO_BROAD,
    useNarrow: false,
    narrowTotalMs: 0,
    totalClusters: SOLFEGGIO_BROAD.length,
  },
  advanced: {
    broad: ADVANCED_BROAD,
    useNarrow: true,
    narrowTotalMs: ADVANCED_NARROW_TOTAL_MS,
    totalClusters: ADVANCED_BROAD.length + NARROW_CLUSTER_COUNT,
  },
};

// Log-spaced band plan: `count` clusters geometrically spaced from start→end so
// each step is the same musical interval (constant frequency ratio), matching
// how pitch is perceived. Ids carry the rounded Hz for traceability.
function logBands(
  startHz: number,
  endHz: number,
  count: number,
  durationMs: number,
  prefix: string,
): FrequencyCluster[] {
  const ratio = (endHz / startHz) ** (1 / (count - 1));
  return Array.from({ length: count }, (_, i) => {
    const hz = Math.round(startHz * ratio ** i);
    return { id: `${prefix}-${hz}`, centerHz: hz, durationMs };
  });
}

type Phase = 'broad' | 'narrow' | 'done';

interface PhaseState {
  phase: Phase;
  clusters: FrequencyCluster[];
  startedAt: number;
}

// Re-center the BioScan baseline on the means measured during the sweep pre-roll
// (position/lighting may have drifted since step 1), while keeping the BioScan
// per-signal sd — 15 s of capture gives a far more robust scale than the 3 s
// pre-roll could. So we correct the offset without inflating z-scores off a
// noisy short-window variance.
function recenterBaseline(base: BaselineStats, mean: SignalDeltas): BaselineStats {
  return {
    facial: { mean: mean.facial, sd: base.facial.sd },
    blink: { mean: mean.blink, sd: base.blink.sd },
    breathing: { mean: mean.breathing, sd: base.breathing.sd },
    voice: { mean: mean.voice, sd: base.voice.sd },
    engagement: { mean: mean.engagement, sd: base.engagement.sd },
  };
}

function SweepPage() {
  const navigate = useNavigate();
  const goal = useSessionStore((s) => s.goal);
  const setResonancePeaks = useSessionStore((s) => s.setResonancePeaks);
  const baseline = useSessionStore((s) => s.baseline);
  // Live capture only when a real BioScan baseline exists and this isn't the
  // synthetic test path. Otherwise the sweep keeps its deterministic fallback.
  const liveMode = baseline !== null && !isSyntheticCapture();
  const { onVideoElement: bindCaptureVideo, getFramesInWindow } = useSweepCapture({
    enabled: liveMode,
  });

  // One-shot mode diagnostic so production telemetry can confirm Phase 3 is
  // genuinely measuring for a given session (never logs raw frames).
  const modeLoggedRef = useRef(false);
  useEffect(() => {
    if (modeLoggedRef.current) return;
    modeLoggedRef.current = true;
    diag('sweep.capture.mode', {
      mode: liveMode ? 'live' : 'synthetic',
      reason: liveMode
        ? 'baseline-present'
        : baseline === null
          ? 'no-baseline'
          : 'synthetic-capture',
    });
  }, [liveMode, baseline]);

  // Gate the tones behind a volume-up instruction so the first sound never
  // catches the user off guard. The synthetic E2E path skips the gate so the
  // deterministic sweep still auto-runs.
  const synthetic = isSyntheticCapture();
  // Synthetic/E2E runs default to the advanced scan so the deterministic path
  // still exercises both the broad and narrow passes.
  const [mode, setMode] = useState<ScanMode>('advanced');
  const config = MODE_CONFIG[mode];
  const [started, setStarted] = useState(synthetic);
  const [phaseState, setPhaseState] = useState<PhaseState>(() => ({
    phase: 'broad',
    clusters: MODE_CONFIG.advanced.broad,
    startedAt: synthetic ? performance.now() : 0,
  }));
  const [broadPeaks, setBroadPeaks] = useState<ResonancePeak[]>([]);
  const [, setNarrowPeaks] = useState<ResonancePeak[]>([]);
  // Baseline re-centered on the pre-roll read; null until the pre-roll closes
  // (or when it yields too few frames, in which case we keep the BioScan one).
  const [insituBaseline, setInsituBaseline] = useState<BaselineStats | null>(null);
  // Effective baseline used for all cluster scoring this sweep.
  const effectiveBaseline = insituBaseline ?? baseline;
  // Pause freezes the whole sweep — tone, ticker, and the measurement clock — so
  // a paused stretch never counts as a (silent) reading. pauseStartRef stamps
  // when we froze; on resume we shift startedAt forward by that gap so elapsed
  // (and every cluster window derived from it) picks up exactly where it left.
  const [paused, setPaused] = useState(false);
  const pauseStartRef = useRef(0);
  // Track which clusters we've already scored — the elapsed-tick approach
  // wants idempotency at the cluster boundary.
  const scoredRef = useRef<Set<string>>(new Set());

  // 200 ms ticker, paused when the document is hidden or the user paused.
  const tick = useTick(phaseState.phase !== 'done' && !paused);
  // While paused, elapsed freezes at the moment we paused (using the stamped
  // pauseStartRef) so phase transitions and scoring don't advance.
  const elapsed = phaseState.startedAt
    ? (paused ? pauseStartRef.current : performance.now()) - phaseState.startedAt
    : 0;
  void tick; // referenced to keep the effect re-running

  // Narrow per-cluster time is derived so the whole narrow pass always lasts
  // config.narrowTotalMs regardless of how many sub-clusters it has — that's
  // what keeps the progress bar advancing at a constant rate to 100%.
  const clusterMs =
    phaseState.phase === 'broad'
      ? BROAD_CLUSTER_MS
      : config.narrowTotalMs / phaseState.clusters.length;
  const clusterIndex =
    phaseState.phase === 'done'
      ? phaseState.clusters.length - 1
      : Math.min(phaseState.clusters.length - 1, Math.floor(elapsed / clusterMs));
  const active = phaseState.clusters[clusterIndex] ?? phaseState.clusters[0];
  if (!active) throw new Error('sweep started with no clusters');
  // One monotonic counter across both passes: broad counts up, then narrow
  // continues from where broad left off, done shows the full count. The
  // denominator is the fixed config.totalClusters, so it never resets or bumps
  // at the broad→narrow hand-off.
  const globalStep =
    phaseState.phase === 'done'
      ? config.totalClusters
      : phaseState.phase === 'broad'
        ? clusterIndex + 1
        : config.broad.length + clusterIndex + 1;
  // Score the just-completed cluster as soon as the ticker advances past it.
  useEffect(() => {
    if (phaseState.phase === 'done') return;
    const lastCompleted = clusterIndex - 1;
    if (lastCompleted < 0) return;
    const cluster = phaseState.clusters[lastCompleted];
    if (!cluster) return;
    const key = `${phaseState.phase}:${cluster.id}:${lastCompleted}`;
    if (scoredRef.current.has(key)) return;
    scoredRef.current.add(key);
    const start = phaseState.startedAt + lastCompleted * clusterMs;
    const frames = liveMode ? getFramesInWindow(start, start + clusterMs) : [];
    const peak = resolveClusterPeak(cluster, frames, effectiveBaseline, goal);
    if (phaseState.phase === 'broad') setBroadPeaks((prev) => [...prev, peak]);
    else setNarrowPeaks((prev) => [...prev, peak]);
  }, [
    clusterIndex,
    phaseState.phase,
    phaseState.clusters,
    phaseState.startedAt,
    clusterMs,
    goal,
    liveMode,
    effectiveBaseline,
    getFramesInWindow,
  ]);

  // Phase transitions: when the elapsed timer passes the full phase budget,
  // score the final cluster and advance.
  useEffect(() => {
    if (phaseState.phase === 'done') return;
    const phaseBudget = phaseState.clusters.length * clusterMs;
    if (elapsed < phaseBudget) return;
    const finalIdx = phaseState.clusters.length - 1;
    const finalCluster = phaseState.clusters[finalIdx];
    if (!finalCluster) return;
    const key = `${phaseState.phase}:${finalCluster.id}:${finalIdx}`;
    if (!scoredRef.current.has(key)) {
      scoredRef.current.add(key);
      const start = phaseState.startedAt + finalIdx * clusterMs;
      const frames = liveMode ? getFramesInWindow(start, start + clusterMs) : [];
      const peak = resolveClusterPeak(finalCluster, frames, effectiveBaseline, goal);
      if (phaseState.phase === 'broad') setBroadPeaks((prev) => [...prev, peak]);
      else setNarrowPeaks((prev) => [...prev, peak]);
    }
    if (phaseState.phase === 'broad' && !config.useNarrow) {
      // Basic scan: single pass. Publish the top-3 responders and finish.
      setBroadPeaks((latest) => {
        const final = selectTopK(rejectLowConfidence(latest), 3);
        setResonancePeaks(final.length > 0 ? final : selectTopK(latest, 3));
        setPhaseState((s) => ({ ...s, phase: 'done' }));
        return latest;
      });
    } else if (phaseState.phase === 'broad') {
      // Use the freshest broadPeaks via setBroadPeaks to compute next-phase
      // clusters — the closure captures the previous value otherwise.
      setBroadPeaks((latest) => {
        const winners = selectNarrowWinners(latest, NARROW_WINNERS);
        const narrowed = narrowClusters(config.broad, winners, {
          stepsPerWinner: NARROW_STEPS_PER_WINNER,
        });
        if (narrowed.length === 0) {
          // Defensive: winners are drawn from broad cluster ids, so a non-empty
          // broad pass always yields narrow clusters. Only an empty broad pass
          // lands here — publish what we have and finish rather than hang.
          setResonancePeaks(selectTopK(latest, 3));
          setPhaseState((s) => ({ ...s, phase: 'done' }));
        } else {
          setPhaseState({
            phase: 'narrow',
            clusters: narrowed,
            startedAt: performance.now(),
          });
        }
        return latest;
      });
    } else {
      // Narrow finished — combine broad + narrow, reject, take top-3, publish.
      setNarrowPeaks((latestNarrow) => {
        const all = [...broadPeaks, ...latestNarrow];
        const final = selectTopK(rejectLowConfidence(all), 3);
        // Fall back to top-3 of all if rejection emptied the list.
        setResonancePeaks(final.length > 0 ? final : selectTopK(all, 3));
        setPhaseState((s) => ({ ...s, phase: 'done' }));
        return latestNarrow;
      });
    }
  }, [
    elapsed,
    phaseState,
    clusterMs,
    config,
    goal,
    setResonancePeaks,
    broadPeaks,
    liveMode,
    effectiveBaseline,
    getFramesInWindow,
  ]);

  // The sweep ends once we transition to 'done'.
  const sweepDone = phaseState.phase === 'done';
  useScanFlowBlocker(!sweepDone);

  // Audio playback — ambient sine + fifth at the active cluster's center.
  const tone = useResonanceTone(active.centerHz, 0.08, 'single');

  // Sound on by default (product requirement). The user reached this screen by
  // clicking through the flow, so the page has sticky activation and
  // AudioContext.resume() succeeds in Chrome/Firefox. Safari may block
  // autoplay — the existing toggle button is the fallback.
  // Tones don't start until the pre-roll has elapsed and the clock is running
  // (startedAt > 0) — that's the window where the user can settle on the dot.
  const toneStartedRef = useRef(false);
  useEffect(() => {
    if (!started || phaseState.startedAt === 0) return;
    if (toneStartedRef.current) return;
    toneStartedRef.current = true;
    void tone.start();
  }, [started, phaseState.startedAt, tone]);

  // Pre-roll: once a mode is picked the dot is shown but the clock stays at 0.
  // After PREROLL_MS we stamp startedAt, which kicks off measurement + tone.
  const prerollStartRef = useRef(0);
  useEffect(() => {
    if (!started || phaseState.startedAt !== 0 || sweepDone) return;
    const id = window.setTimeout(() => {
      const now = performance.now();
      // Re-center the baseline on the just-finished pre-roll window so the read
      // is scored against where the user is sitting *now*, not their BioScan
      // position. Falls back to the BioScan baseline when too few frames landed.
      if (liveMode && baseline) {
        const prerollFrames = getFramesInWindow(prerollStartRef.current, now);
        if (prerollFrames.length >= MIN_PREROLL_FRAMES) {
          const mean = faceSummaryToSignalDeltas(summarizeFace(prerollFrames));
          setInsituBaseline(recenterBaseline(baseline, mean));
        }
      }
      setPhaseState((s) => ({ ...s, startedAt: now }));
    }, PREROLL_MS);
    return () => window.clearTimeout(id);
  }, [started, phaseState.startedAt, sweepDone, liveMode, baseline, getFramesInWindow]);
  // True while the dot is up but the clock hasn't started — the settle window.
  const prerolling = started && phaseState.startedAt === 0 && !sweepDone;
  const prerollLeft = prerolling
    ? Math.max(1, Math.ceil((PREROLL_MS - (performance.now() - prerollStartRef.current)) / 1000))
    : 0;

  // Show the dot for the pre-roll first; the clock + tone start when it ends.
  const begin = (selected: ScanMode) => {
    setMode(selected);
    prerollStartRef.current = performance.now();
    setStarted(true);
    setInsituBaseline(null);
    setPhaseState({
      phase: 'broad',
      clusters: MODE_CONFIG[selected].broad,
      startedAt: 0,
    });
  };

  // Pause/resume the whole sweep. On resume we push startedAt forward by the
  // paused duration so the clock continues seamlessly, then restart the tone.
  const togglePause = () => {
    if (paused) {
      const pausedFor = performance.now() - pauseStartRef.current;
      setPhaseState((s) => (s.startedAt === 0 ? s : { ...s, startedAt: s.startedAt + pausedFor }));
      setPaused(false);
      void tone.start();
    } else {
      pauseStartRef.current = performance.now();
      setPaused(true);
      tone.stop();
    }
  };

  // Re-scan: drop everything and return to the Basic/Advanced choice.
  const rescan = () => {
    tone.stop();
    toneStartedRef.current = false;
    scoredRef.current = new Set();
    setBroadPeaks([]);
    setNarrowPeaks([]);
    setPaused(false);
    setStarted(false);
    setPhaseState({ phase: 'broad', clusters: MODE_CONFIG.advanced.broad, startedAt: 0 });
  };

  // Silence the tone once the sweep finishes.
  useEffect(() => {
    if (sweepDone) tone.stop();
  }, [sweepDone, tone]);

  const overallProgress = useMemo(
    () =>
      sweepProgress({
        phase: phaseState.phase,
        elapsedMs: elapsed,
        broadBudgetMs: config.broad.length * BROAD_CLUSTER_MS,
        // Fixed-duration narrow pass: actual budget always equals the max, so
        // the bar moves at one constant rate through both passes. Basic mode has
        // no narrow pass (0), which makes the broad pass fill the bar 0→100%.
        narrowBudgetMs: config.narrowTotalMs,
        maxNarrowBudgetMs: config.narrowTotalMs,
      }),
    [elapsed, phaseState, config],
  );

  if (!started) {
    return (
      <>
        <FlowActionBar back={{ to: '/scan/voice' }} />
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 pb-28 pt-6">
          <div>
            <ScanProgress current={3} steps={SCAN_STEPS} />
          </div>
          <section
            className="mx-auto flex w-full max-w-2xl flex-col items-center gap-5 rounded-lg border border-hairline bg-surface-1 p-8 text-center"
            style={{ boxShadow: 'var(--lift-1)' }}
          >
            <span className="flex size-14 items-center justify-center rounded-full bg-surface-2 text-aurora">
              <Volume2 size={28} strokeWidth={1.75} aria-hidden="true" />
            </span>
            <h1 className="font-display text-3xl text-ink-0">{copy.scan.sweepReadyTitle}</h1>
            <p className="text-ink-2">{copy.scan.sweepReadyBody}</p>
            {/* Single Solfeggio (basic) pass — the advanced/narrow mode is kept in
                the code but is no longer offered as a choice. */}
            <div className="flex w-full flex-col gap-2 pt-1">
              <button
                type="button"
                onClick={() => begin('basic')}
                className={buttonClasses({ variant: 'resonance', size: 'lg' })}
              >
                Begin scan
              </button>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                ~30 seconds
              </span>
            </div>
          </section>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Advance lives in the centre section (with the focus dot) once the sweep
          completes, so the CTA sits where the user is already looking — only
          Back stays in the bottom bar. */}
      <FlowActionBar back={{ to: '/scan/voice' }} />
      <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col gap-8 px-6 pb-28 pt-6">
        <div className="shrink-0">
          <ScanProgress current={3} steps={SCAN_STEPS} />
        </div>

        {/* Screen reader announcement of sweep progress (visuals stay calm). */}
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {sweepDone
            ? 'Sweep complete. You can view results.'
            : `Listening, step ${globalStep} of ${config.totalClusters}.`}
        </p>

        {/* Capture keeps running for the pupil/gaze read, but the camera view is
            hidden so the user can rest their eyes on the focus dot. display:none
            would pause video decoding, so keep it laid out but invisible. */}
        {liveMode && (
          <div
            aria-hidden="true"
            className="pointer-events-none fixed left-0 top-0 -z-10 size-px overflow-hidden opacity-0"
          >
            <WebcamPreview autoGrant compact onVideoElement={bindCaptureVideo} />
          </div>
        )}

        {/* Focus dot — the single thing the user looks at during the sweep. */}
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <p className="text-center text-lg text-ink-2">
            {sweepDone
              ? 'All done — you can view your results.'
              : prerolling
                ? `Rest your eyes on the dot — sound starts in ${prerollLeft}…`
                : 'Keep your eyes on the dot.'}
          </p>
          {/* Pre-roll is the only window to fix capture quality before the read
              starts — the pupil/gaze signal is read against this position. */}
          {prerolling && liveMode && (
            <p className="text-center text-sm text-ink-3">Move closer, hold steady, good light.</p>
          )}
          {sweepDone ? (
            <button
              type="button"
              onClick={() => navigate({ to: '/scan/results' })}
              className={`${buttonClasses({ variant: 'resonance', size: 'lg' })} group shadow-xl shadow-abyss/30`}
            >
              <span>See results</span>
              <ArrowRight
                size={16}
                strokeWidth={1.8}
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </button>
          ) : (
            // Static dot — no pulsing or luminance change, so the steady screen
            // brightness doesn't drive the pupil dilation we're measuring.
            <div className="flex size-16 items-center justify-center">
              <span
                className="qi-focus-dot inline-flex size-6 rounded-full"
                style={{
                  background: 'var(--aurora)',
                  boxShadow: '0 0 24px color-mix(in oklab, var(--aurora) 70%, transparent)',
                }}
              />
            </div>
          )}
        </div>

        {/* Minimal progress + sound toggle */}
        <div className="shrink-0 space-y-2">
          <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
            <div className="flex items-center gap-4">
              {!prerolling && (
                <button
                  type="button"
                  onClick={togglePause}
                  className="uppercase tracking-[0.18em] transition-colors hover:text-ink-0"
                >
                  {paused ? 'Resume' : 'Pause'}
                </button>
              )}
              <button
                type="button"
                onClick={rescan}
                className="uppercase tracking-[0.18em] transition-colors hover:text-ink-0"
              >
                Re-scan
              </button>
            </div>
            <span>{Math.round(overallProgress * 100)}%</span>
          </div>
          <div className="h-1 overflow-hidden rounded-pill bg-surface-3">
            <div
              className="h-full origin-left rounded-pill"
              style={{
                transform: `scaleX(${overallProgress})`,
                transition: 'transform 200ms linear',
                background: 'linear-gradient(90deg, var(--violet), var(--aurora))',
                boxShadow: '0 0 12px color-mix(in oklab, var(--aurora) 50%, transparent)',
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * 200ms ticker. Pauses when the document is hidden so background tabs don't
 * burn battery and the sweep clock doesn't fast-forward when the user
 * tabs back in. Caller can force-pause by passing `enabled = false`.
 */
function useTick(enabled = true): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let id: number | null = null;
    const start = () => {
      if (id !== null) return;
      id = window.setInterval(() => setTick((t) => t + 1), 200);
    };
    const stop = () => {
      if (id === null) return;
      window.clearInterval(id);
      id = null;
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [enabled]);
  return tick;
}
